import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CONSENT_STORAGE_KEY = 'cookie_consent';

// Consent types
export const CONSENT_TYPES = {
  ACCEPTED_ALL: 'accepted_all',
  NECESSARY_ONLY: 'necessary_only',
  CUSTOM: 'custom',
};

// Default consent state
const defaultConsentState = {
  hasConsented: false,
  consentType: null,
  preferences: {
    necessary: true, // Always true, cannot be disabled
    statistics: false,
  },
  timestamp: null,
};

const CookieConsentContext = createContext(null);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(defaultConsentState);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setConsent(parsed);
        setShowBanner(false);
      } catch (e) {
        console.error('Error parsing stored consent:', e);
        setShowBanner(true);
      }
    } else {
      // Show banner after a small delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Save consent to localStorage whenever it changes
  const saveConsent = useCallback((newConsent) => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsent));
    setConsent(newConsent);
    setShowBanner(false);
    setShowModal(false);
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newConsent = {
      hasConsented: true,
      consentType: CONSENT_TYPES.ACCEPTED_ALL,
      preferences: {
        necessary: true,
        statistics: true,
      },
      timestamp: new Date().toISOString(),
    };
    saveConsent(newConsent);
  }, [saveConsent]);

  // Accept only necessary cookies
  const acceptNecessaryOnly = useCallback(() => {
    const newConsent = {
      hasConsented: true,
      consentType: CONSENT_TYPES.NECESSARY_ONLY,
      preferences: {
        necessary: true,
        statistics: false,
      },
      timestamp: new Date().toISOString(),
    };
    saveConsent(newConsent);
  }, [saveConsent]);

  // Save custom preferences
  const saveCustomPreferences = useCallback((preferences) => {
    const newConsent = {
      hasConsented: true,
      consentType: CONSENT_TYPES.CUSTOM,
      preferences: {
        necessary: true, // Always true
        statistics: preferences.statistics ?? false,
      },
      timestamp: new Date().toISOString(),
    };
    saveConsent(newConsent);
  }, [saveConsent]);

  // Open settings modal
  const openSettings = useCallback(() => {
    setShowModal(true);
  }, []);

  // Close settings modal
  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  // Check if analytics can be loaded
  const canLoadAnalytics = consent.hasConsented && consent.preferences.statistics;

  // Reset consent (for testing or user request)
  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    setConsent(defaultConsentState);
    setShowBanner(true);
  }, []);

  const value = {
    consent,
    showBanner,
    showModal,
    acceptAll,
    acceptNecessaryOnly,
    saveCustomPreferences,
    openSettings,
    closeModal,
    canLoadAnalytics,
    resetConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentContext;
